import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaCog, FaArrowLeft, FaSave, FaPlus, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getPostOrderSettings, updatePostOrderSettings } from '../../services/ClientService';

const PostOrderSettings = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [settings, setSettings] = useState({
        returnWindow: { value: 7, unit: 'days' },
        autoApproveReturns: false,
        requireQC: true,
        refundMethods: ['original_payment', 'store_credit'],
        returnReasons: [
            'Wrong item received',
            'Item damaged during delivery',
            'Item not as described',
            'Quality not as expected',
            'Size/fit issue',
            'Changed my mind',
            'Other'
        ],
        returnPolicy: 'Returns accepted within 7 days of delivery. Items must be unused and in original packaging.'
    });
    const [newReason, setNewReason] = useState('');

    const fetchSettings = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getPostOrderSettings();
            if (response?.success && response.data) {
                setSettings(prev => ({ ...prev, ...response.data }));
            }
        } catch (error) {
            console.error('Failed to load settings:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    const handleSave = async () => {
        try {
            setSaving(true);
            const response = await updatePostOrderSettings(settings);
            if (response?.success) {
                toast.success('Settings saved successfully');
            } else {
                toast.error(response?.message || 'Failed to save settings');
            }
        } catch (error) {
            toast.error('Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    const addReason = () => {
        if (newReason.trim() && !settings.returnReasons.includes(newReason.trim())) {
            setSettings(prev => ({
                ...prev,
                returnReasons: [...prev.returnReasons, newReason.trim()]
            }));
            setNewReason('');
        }
    };

    const removeReason = (index) => {
        setSettings(prev => ({
            ...prev,
            returnReasons: prev.returnReasons.filter((_, i) => i !== index)
        }));
    };

    const toggleRefundMethod = (method) => {
        setSettings(prev => {
            const methods = prev.refundMethods.includes(method)
                ? prev.refundMethods.filter(m => m !== method)
                : [...prev.refundMethods, method];
            return { ...prev, refundMethods: methods };
        });
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="w-8 h-8 border-3 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-6 py-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate(-1)}
                            className="p-2 rounded-lg hover:bg-gray-100 transition-all"
                        >
                            <FaArrowLeft className="text-gray-600" />
                        </button>
                        <div>
                            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                                <FaCog className="text-gray-600" />
                                Post-Order Settings
                            </h1>
                            <p className="text-sm text-gray-500">Configure return policies, approval flows, and refund methods</p>
                        </div>
                    </div>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-all text-sm font-medium disabled:opacity-50"
                    >
                        {saving ? (
                            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                        ) : (
                            <FaSave />
                        )}
                        Save Settings
                    </button>
                </div>
            </div>

            <div className="max-w-3xl mx-auto px-6 py-6 space-y-6">
                {/* Return Window */}
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Return Window</h2>
                    <p className="text-sm text-gray-500 mb-3">How long after delivery can customers request a return?</p>
                    <div className="flex items-center gap-3">
                        <input
                            type="number"
                            min="1"
                            value={settings.returnWindow.value}
                            onChange={(e) => setSettings(prev => ({
                                ...prev,
                                returnWindow: { ...prev.returnWindow, value: parseInt(e.target.value) || 1 }
                            }))}
                            className="w-24 border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
                        />
                        <select
                            value={settings.returnWindow.unit}
                            onChange={(e) => setSettings(prev => ({
                                ...prev,
                                returnWindow: { ...prev.returnWindow, unit: e.target.value }
                            }))}
                            className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
                        >
                            <option value="hours">Hours</option>
                            <option value="days">Days</option>
                        </select>
                    </div>
                </motion.div>

                {/* Auto Approve & QC */}
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Approval & Quality Check</h2>
                    <div className="space-y-4">
                        <label className="flex items-center justify-between cursor-pointer">
                            <div>
                                <p className="font-medium text-gray-800">Auto-approve returns</p>
                                <p className="text-sm text-gray-500">Automatically approve all return requests without manual review</p>
                            </div>
                            <div
                                onClick={() => setSettings(prev => ({ ...prev, autoApproveReturns: !prev.autoApproveReturns }))}
                                className={`w-12 h-7 rounded-full transition-all cursor-pointer flex items-center ${
                                    settings.autoApproveReturns ? 'bg-indigo-600 justify-end' : 'bg-gray-300 justify-start'
                                }`}
                            >
                                <div className="w-5 h-5 bg-white rounded-full shadow-md mx-1"></div>
                            </div>
                        </label>
                        <label className="flex items-center justify-between cursor-pointer">
                            <div>
                                <p className="font-medium text-gray-800">Require quality check</p>
                                <p className="text-sm text-gray-500">Require QC inspection before processing refund</p>
                            </div>
                            <div
                                onClick={() => setSettings(prev => ({ ...prev, requireQC: !prev.requireQC }))}
                                className={`w-12 h-7 rounded-full transition-all cursor-pointer flex items-center ${
                                    settings.requireQC ? 'bg-indigo-600 justify-end' : 'bg-gray-300 justify-start'
                                }`}
                            >
                                <div className="w-5 h-5 bg-white rounded-full shadow-md mx-1"></div>
                            </div>
                        </label>
                    </div>
                </motion.div>

                {/* Refund Methods */}
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Refund Methods</h2>
                    <p className="text-sm text-gray-500 mb-3">Select the refund methods available to customers</p>
                    <div className="flex flex-wrap gap-3">
                        {[
                            { key: 'original_payment', label: 'Original Payment Method' },
                            { key: 'store_credit', label: 'Store Credit' },
                            { key: 'bank_transfer', label: 'Bank Transfer' }
                        ].map((method) => (
                            <button
                                key={method.key}
                                onClick={() => toggleRefundMethod(method.key)}
                                className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all border ${
                                    settings.refundMethods.includes(method.key)
                                        ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                                        : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                                }`}
                            >
                                {method.label}
                            </button>
                        ))}
                    </div>
                </motion.div>

                {/* Return Reasons */}
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Return Reasons</h2>
                    <p className="text-sm text-gray-500 mb-3">Predefined reasons customers can choose from</p>
                    <div className="space-y-2 mb-4">
                        {settings.returnReasons.map((reason, index) => (
                            <div key={index} className="flex items-center justify-between px-4 py-2.5 bg-gray-50 rounded-xl">
                                <span className="text-sm text-gray-700">{reason}</span>
                                <button
                                    onClick={() => removeReason(index)}
                                    className="p-1 text-gray-400 hover:text-red-500 transition-all"
                                >
                                    <FaTrash className="text-xs" />
                                </button>
                            </div>
                        ))}
                    </div>
                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={newReason}
                            onChange={(e) => setNewReason(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && addReason()}
                            placeholder="Add a new reason..."
                            className="flex-1 border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
                        />
                        <button
                            onClick={addReason}
                            className="px-4 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-all text-sm"
                        >
                            <FaPlus />
                        </button>
                    </div>
                </motion.div>

                {/* Return Policy */}
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Return Policy Text</h2>
                    <p className="text-sm text-gray-500 mb-3">Displayed to customers when requesting a return</p>
                    <textarea
                        value={settings.returnPolicy}
                        onChange={(e) => setSettings(prev => ({ ...prev, returnPolicy: e.target.value }))}
                        rows={4}
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 resize-none"
                    />
                </motion.div>
            </div>
        </div>
    );
};

export default PostOrderSettings;
